





















































































































import { Vue, Component, Watch } from 'vue-property-decorator'
import { mapGetters } from 'vuex'
import { AxiosResponse } from 'axios'
import { RawLocation } from 'vue-router'
import SpinnerPopup from '@/components/shared/SpinnerPopup'
import LoginDialog from '@/components/LoginDialog.vue'
import StatusIcon from '@/components/StatusIcon.vue'
import { About, ISnackbar, UserData } from '@/model/index'
import { Pages } from '@/pages'
import auth from '@/libs/auth.js'
import { LicenseUtil } from '@/api/license'
import { get } from '@/api/utils/http-utils'
import { errorHandler } from '@/api/utils/http-helpers'
import { checkLanguage } from '@/lang-check'
import { userProfiles } from '@/api/const'
import { permissions } from '@/userUtils'
import WebSocketService from '@/services/WebsocketService'

@Component ({
  components: {
    StatusIcon: StatusIcon,
    SpinnerPopup: SpinnerPopup,
    LoginDialog: LoginDialog
  },
  computed: {
    ...mapGetters('user', [ 'userData', 'invalidCredentials', 'allUserProfiles', 'userPermissions' ]),
    ...mapGetters('settings', ['isDbDisconnected', 'statusSocketServer', 'isPatternValidationEnabled']),
    ...mapGetters('environment', ['isLicenseValid'])
  }
})
export default class App extends Vue {

  private licenseVerified: boolean = false
  private loadingLicense: boolean = false
  private app: string = 'coboware'
  private about: About = {}
  private userData!: UserData
  private userPermissions!: string[]
  private allUserProfiles!: UserData[]
  private showLoginPopup: boolean = false
  private notAuthenticated: boolean = true
  private invalidCredentials!: boolean
  private userProfilesList = userProfiles
  private menuItems: any[] = [
    { id: 2, title: 'userMenu.userManagement', params: [Pages.USERMANAGEMENT.routeLink], action: this.route },
    { id: 4, title: 'userMenu.settings', params: [Pages.SETTINGS.routeLink], action: this.route },
    { id: 6, title: 'userMenu.machinesettings', params: [Pages.MACHINESETTINGS.routeLink], action: this.route },
    { id: 100, title: 'userMenu.license', params: [ Pages.LICENSE.routeLink ], action: this.route },
    { id: 101, title: 'userMenu.backupRestore', params: [ Pages.BACKUPRESTORE.routeLink ], action: this.route },
    { id: 103, title: 'userMenu.auditTrail', params: [ Pages.AUDITTRAIL.routeLink ], action: this.route },
    { id: 102, title: 'userMenu.about', params: [ Pages.ABOUT.routeLink ], action: this.route },
  ]
  private snackbar: ISnackbar = {
    show: false,
    color: 'info',
    text: '',
    timeout: 1000
  }
  private webSocketService: any
  private isDbDisconnected!: boolean
  private statusSocketServer!: string
  private isPatternValidationEnabled!: boolean
  private isLicenseValid!: boolean

  get isMasterLicense (): boolean {
    const license = this.$store.state.environment.license
    return license && license.master === true
  }

  created() {
    this.loadingLicense = true
    this.$i18n.locale = checkLanguage(String(this.$i18n.fallbackLocale))
  }

  async mounted() {
    await this.checkBackendStatus ()
    this.initWebSocket()
    this.detectChromeKioskMode ()
    await this.initAllUserProfiles().then(() => {
      this.checkUserInfo().then(() => {
        this.loginDefaultUser()
      })
    })
  }

  beforeDestroy() {
    if (this.webSocketService) {
      this.webSocketService.close()
    }
  }

  private async checkBackendStatus() {
    const url = '/about'
    console.log('Checking backend status. Last status was: ', (this.about) ? this.about.status : '')
    get(url)
      .then((resp: AxiosResponse) => {
        this.about = Object.assign({}, resp.data as About)
      })
      .catch((e: any) => {
        this.about = {}
        errorHandler(e)
      })
      .finally(() => {
        this.loadLicenseIfBackendIsRunning()
      })
  }

  private initWebSocket() {
    if ('https:' === window.location.protocol) {
      return
    }
    const websocketUrl = this.getWebsocketUrl()
    this.webSocketService = new WebSocketService(websocketUrl)

    this.webSocketService.on('open', () => {
      console.debug('WebSocket connected')
    })

    this.webSocketService.on('message', (data: any) => {
      this.handleMessage(data) // Handle incoming data
    })

    this.webSocketService.on('close', () => {
      console.debug('WebSocket connection closed')
    })

    this.webSocketService.on('error', (error: any) => {
      console.error('WebSocket error:', error)
    })

    this.webSocketService.on('reconnect_failed', (info: any) => {
      console.error('WebSocket reconnection failed:', info)
    })

    this.webSocketService.initialize()
  }

  private getWebsocketUrl() {
    switch (process.env.NODE_ENV) {
      case 'development':
        return 'ws://localhost:5567/ws'
      case 'test':
        return 'ws://localhost:1234'
      default:
        return 'ws://' +  window.location.host + '/ws'
    }
  }

  private async initAllUserProfiles() {
    await this.$store.dispatch('user/getAllUserProfiles')
  }

  private showLogin() {
    this.showLoginPopup = true
  }

  private authSignOut() {
    auth.signOut()
  }

  private async logoutUser() {
    if (this.userData.userName === this.getDefaultUserName()) {
      await this.$store.dispatch('user/logoutUser')
      await this.logInUser('basic')
      return
    }
    await this.$store.dispatch('user/logoutUser')
    await this.loginDefaultUser()
    this.$router.push('/')
  }

  private async loginDefaultUser() {
    if (this.userData && Object.keys(this.userData).length > 0) {
      return
    }
    const name = this.getDefaultUserName()
    await this.logInUser(name)
  }

  private async checkUserInfo() {
    if (!this.userData || Object.keys(this.userData).length === 0) {
      await this.$store.dispatch('user/getUserInfo')
    }
  }

  private async logInUser(name: string) {
    const credentials = {
      userName: name,
      password: ''
    }
    await this.$store.dispatch('user/userLogin', credentials)
    this.$emit('login')
  }

  private getDefaultUserName(): string {
    const usersWithNoNeedToLogin = this.allUserProfiles.filter(p => !p.loginRequired)
    return usersWithNoNeedToLogin.find(u => u.userName === 'supervisor') != null ? 'supervisor'
        : usersWithNoNeedToLogin.find(u => u.userName === 'operator') != null ? 'operator'
            : 'basic'
  }

  private login() {
    this.showLoginPopup = this.invalidCredentials
  }

  private cancelLogin() {
    this.showLoginPopup = false
  }

  private displayUserProfileName () {
    return this.userProfilesList.find(u => u.id === this.userData.userName)!.name
  }

  @Watch ('userData.userName')
  private userNameWatcher() {
    this.notAuthenticated = this.userData!.userName === '' || this.userData!.userName === 'basic'
  }

  private showAsDisabled(menuItemId: number) {
    if (menuItemId === 2 && !this.userPermissions.includes(permissions.MODIFYUSERMANAGEMENT)) {
      return true
    }
    if (menuItemId === 6 && !this.userPermissions.includes(permissions.MODIFYMACHINESETTINGS)
        && !this.userPermissions.includes(permissions.MODIFYGRIPPERMACHINESETTINGS)) {
      return true
    }
    if (menuItemId === 103 && !this.userPermissions.includes(permissions.VIEWAUDITTRAIL)) {
      return true
    }
    return false
  }

  get usersFullName (): string {
    return auth.user!.name || ''
  }

  get username () {
    return auth.user!.preferred_username || ''
  }

  get isExternalUser (): boolean {
    return auth.user!.external || false
  }

  private loadLicenseIfBackendIsRunning() {
    if (this.about && this.about.status === About.StatusEnum.RUNNING) {
      console.log('Backend is running')
      this.loadLicense()
    } else {
      // Backend is not running. Check status again in 10 seconds
      setTimeout(() => { this.checkBackendStatus() }, 10000)
    }
  }

  private loadLicense() {
    this.$store.dispatch('settings/loadSettings')
    this.$store.dispatch('machinesettings/loadMachineSettings')
    this.$store.dispatch('machinesettings/getFixedMachineSettings')
    this.$store.dispatch('palletpatterns/loadPalletPatterns')
    LicenseUtil.tryLocalAsync(this.app)
      .then((resp: AxiosResponse) => {
        this.licenseVerified = LicenseUtil.isValid(resp.data)
        console.debug('[DEBUG] App mounted! licenseVerified:', this.licenseVerified)
        if (this.licenseVerified) {
          this.$store.commit('environment/LICENSE_UPDATED', resp.data)
        }
      })
      .finally(() => {
        this.loadingLicense = false
        this.$store.commit('environment/LICENSE_LOADED')
      })
  }

  private detectChromeKioskMode () {
    const isKioskMode = auth == null || auth.server == null // currently only demo & beta environment use SSO
    if (isKioskMode) {
      this.chromeKioskModeDetected()
    } else {
      this.$store.commit('environment/CHROME_KIOSK_MODE_DETECTED', false)
    }
  }

  private chromeKioskModeDetected () {
    this.$store.commit('environment/CHROME_KIOSK_MODE_DETECTED', true)
    this.fixVirtualKeyboardEventForVue()
  }

  private fixVirtualKeyboardEventForVue () {
    console.log('Add event listener that will dispatch input events on keyup')
    window.addEventListener('keyup', event => {
      if (event.target instanceof HTMLInputElement) {
        // dispatch an input event to text field every time we have a keyup. this is a work around
        // for a problem with chrome extension "Virtual Keyboard" not updating Vue input fields.
        // Vue input fields expect to have an input event and not just keypress and keyup.
        const inputElement = event.target
        const value = inputElement.value
        const inputEvent = document.createEvent('HTMLEvents')
        inputEvent.initEvent('input', true, true)
        // console.log('App.vue will dispatch input event "' + value + '" to HTMLInputElement')
        inputElement.dispatchEvent(inputEvent)
      }
    })
  }

  private async route (link: RawLocation) {
    const name = typeof link === 'object' && 'name' in link ? String(link.name).toLowerCase() : ''
    if (name === 'usermanagement') {
      if (!this.userPermissions.includes(permissions.MODIFYUSERMANAGEMENT)) {
        this.commitPreselectedUserProfile('maintenance').then(() => {
          this.showLogin()
        })
        return
      }
    }
    if (name === 'machinesettings') {
      if (!this.userPermissions.includes(permissions.MODIFYMACHINESETTINGS)
          && !this.userPermissions.includes(permissions.MODIFYGRIPPERMACHINESETTINGS)) {
        await this.commitPreselectedUserProfile('maintenance').then(() => {
          this.showLogin()
        })
        return
      }
    }
    if (name === 'audittrail') {
      if (!this.userPermissions.includes(permissions.VIEWAUDITTRAIL)) {
        await this.commitPreselectedUserProfile('maintenance').then(() => {
          this.showLogin()
        })
        return
      }
    }
    this.$router.push(link)
  }

  private async commitPreselectedUserProfile(profile: string) {
    this.$store.commit('user/setPreselectedUserProfile', profile)
  }

  private handleMessage(message: any) {
    if (message.type === 'pattern_validation_progress') {
      this.$store.dispatch('palletpatterns/setValidationProgress', message)
      return
    }
    if (message.type === 'database_status') {
      if (this.snackbar.show) {
        this.snackbar.show = false
      }
      const dbDisconnected = this.isDbDisconnected
      this.$nextTick( () => {
        if (message.status === 'unavailable' && !dbDisconnected) {
          this.snackbar.show = true
          this.snackbar.color = 'error'
          this.snackbar.timeout = 0
          this.snackbar.text = ` ${this.localText('general.databaseConnectionIssue')} ${this.localText('general.messageRemainsContactSupport')}`
          this.$store.dispatch('settings/setDbDisconnected', true)
        } else if (message.status === 'available' && dbDisconnected) {
          this.snackbar.show = true
          this.snackbar.color = 'success'
          this.snackbar.timeout = 2000
          this.snackbar.text = this.localText('general.databaseConnectionRecovered')
          this.$store.dispatch('settings/setDbDisconnected', false)
          if (!this.isLicenseValid) {
            this.loadLicense()
          }
        } else if (message.status === 'switched') {
          setTimeout(() => {
            window.location.reload()
          }, 500)
        }
      })
    }
    if (message.type === 'socket_server_status') {
      const currentStatus = this.$store.state.settings.statusSocketServer
      const status = message.status
      if (currentStatus === status) {
        return
      }
      this.$nextTick( () => {
        if (message.status === 'unavailable') {
          this.snackbar.show = true
          this.snackbar.color = 'error'
          this.snackbar.timeout = 0
          this.snackbar.text = this.localText('general.patternValidationUnavailable')
        } else if (message.status === 'available' && currentStatus === 'unavailable') {
          this.snackbar.show = true
          this.snackbar.color = 'success'
          this.snackbar.timeout = 2000
          this.snackbar.text = this.localText('general.patternValidationAvailable')
        }
        this.$store.dispatch ('settings/setStatusSocketServer', status)
      })
    }
  }

  private localText (arg: string, values?: string[]): string {
    return String(this.$t(arg, values))
  }
}
